<template>
  <section class="min-h-screen bg-[#0b0e13] px-3 pb-16 pt-3 text-white sm:px-6 sm:pb-28 sm:pt-8" :data-locale="$i18n.locale">
    <div class="mx-auto max-w-6xl space-y-4 sm:space-y-6">
      <div class="overflow-hidden rounded-[1.75rem] border border-white/10 bg-[radial-gradient(circle_at_top_left,_rgba(195,255,77,0.14),_transparent_38%),linear-gradient(160deg,_rgba(18,22,30,0.96),_rgba(11,14,19,0.98))] p-4 shadow-glow sm:rounded-[2rem] sm:p-8">
        <div class="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div class="flex flex-col items-center gap-4 text-center sm:flex-row sm:items-start sm:gap-5 sm:text-left">
            <div class="h-16 w-16 overflow-hidden rounded-[1.35rem] border border-white/10 bg-midnight-900/70 sm:h-24 sm:w-24 sm:rounded-[1.75rem]">
              <img
                v-if="displayAvatarUrl"
                :src="displayAvatarUrl"
                alt="Profile avatar"
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-2xl font-semibold text-steel-200 sm:text-3xl">
                {{ initials }}
              </div>
            </div>
            <div class="space-y-2">
              <p class="text-xs uppercase tracking-[0.35em] text-steel-400">{{ $t('settings.eyebrow') }}</p>
              <h1 class="font-display text-2xl font-semibold tracking-tight text-white sm:text-4xl">
                {{ $t('settings.title') }}
              </h1>
              <p class="max-w-2xl text-sm leading-6 text-steel-300 sm:leading-6">
                {{ $t('settings.description') }}
              </p>
              <div class="flex flex-wrap items-center justify-center gap-2 pt-1 text-xs text-steel-300 sm:justify-start">
                <span class="max-w-full truncate rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5">{{ email }}</span>
                <span class="rounded-full border px-3 py-1.5" :class="roleBadgeClass">{{ roleLabel }}</span>
                <span class="rounded-full border px-3 py-1.5" :class="verifiedBadgeClass">
                  {{ verified ? $t('settings.verifiedActive') : $t('settings.notVerified') }}
                </span>
                <span class="rounded-full border px-3 py-1.5" :class="championBadgeClass">
                  {{ championsLeagueWinner ? $t('settings.championActive') : $t('settings.noChampion') }}
                </span>
              </div>
            </div>
          </div>

          <div class="grid w-full gap-3 sm:grid-cols-2 lg:w-[23rem]">
            <router-link
              :to="publicProfilePath"
              class="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition hover:border-white/30 hover:bg-white/10"
            >
              {{ $t('settings.viewPublicProfile') }}
            </router-link>
            <router-link
              to="/change-password"
              class="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition hover:border-white/30 hover:bg-white/10"
            >
              {{ $t('settings.changePassword') }}
            </router-link>
            <label class="inline-flex cursor-pointer items-center justify-center rounded-2xl border border-neon-400/30 bg-neon-400/10 px-4 py-3 text-sm font-semibold text-neon-400 transition hover:border-neon-400/50 hover:bg-neon-400/15 sm:col-span-2">
              <input class="hidden" type="file" accept="image/*" @change="handleAvatarUpload" />
              {{ $t('settings.uploadProfilePhoto') }}
            </label>
          </div>
        </div>
      </div>

      <div
        v-if="status === 'banned'"
        class="rounded-3xl border border-rose-500/40 bg-rose-500/10 px-5 py-4 text-sm text-rose-100"
      >
        {{ $t('settings.bannedMessage') }}
      </div>

      <div class="rounded-[1.75rem] border border-white/10 bg-[#10141c] p-5 shadow-glow sm:rounded-[2rem] sm:p-6">
        <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div class="max-w-2xl">
            <p class="text-xs uppercase tracking-[0.32em] text-steel-400">{{ $t('settings.languageEyebrow') }}</p>
            <h2 class="mt-2 text-2xl font-semibold text-white">{{ $t('settings.languageTitle') }}</h2>
            <p class="mt-2 text-sm leading-6 text-steel-300">{{ $t('settings.languageDescription') }}</p>
          </div>
          <label class="block min-w-[16rem] text-sm text-steel-200">
            {{ $t('settings.languageSelectLabel') }}
            <select
              :value="currentLocale"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/70 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              @change="handleLocaleChange"
            >
              <option v-for="option in languageOptions" :key="option.code" :value="option.code">
                {{ option.label }}
              </option>
            </select>
          </label>
        </div>
        <p class="mt-4 text-xs uppercase tracking-[0.22em] text-steel-400">{{ $t('settings.languageHelp') }}</p>
      </div>

      <div class="z-20 sm:hidden">
        <div class="rounded-[1.5rem] border border-white/10 bg-[#10141c]/95 p-3 shadow-glow backdrop-blur">
          <div class="flex flex-col gap-3">
            <div class="min-w-0">
              <p class="text-sm font-semibold text-white">{{ $t('settings.saveCardTitle') }}</p>
              <p v-if="statusMessage" class="mt-1 text-sm text-steel-300">{{ statusMessage }}</p>
              <p v-else class="mt-1 text-sm text-steel-400">{{ $t('settings.saveCardDescription') }}</p>
            </div>
            <button
              type="button"
              class="w-full rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
              :disabled="isSaving"
              @click="saveProfile"
            >
              {{ isSaving ? $t('settings.saving') : $t('settings.saveChanges') }}
            </button>
          </div>
          <p v-if="errorMessage" class="mt-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
            {{ errorMessage }}
          </p>
        </div>
      </div>

      <div class="grid gap-4 sm:gap-6 xl:grid-cols-[1.45fr_0.95fr]">
        <div class="space-y-6">
          <div class="overflow-hidden rounded-[1.75rem] border border-white/10 bg-[#10141c] shadow-glow sm:rounded-[2rem]">
            <div
              class="relative min-h-[15rem] bg-[radial-gradient(circle_at_top_left,_rgba(125,249,255,0.16),_transparent_32%),radial-gradient(circle_at_bottom_right,_rgba(251,191,36,0.16),_transparent_32%),linear-gradient(150deg,_rgba(13,18,27,0.98),_rgba(17,24,39,0.94))]"
            >
              <img
                v-if="displayCoverUrl"
                :src="displayCoverUrl"
                alt="Profile cover"
                class="absolute inset-0 h-full w-full object-cover"
                :style="{ objectPosition: coverObjectPosition }"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-[#10141c] via-[#10141c]/35 to-transparent"></div>
              <div class="relative flex min-h-[15rem] flex-col justify-between p-5 sm:p-6">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <p class="text-xs uppercase tracking-[0.32em] text-steel-300">Profile Cover</p>
                    <h2 class="mt-2 text-2xl font-semibold text-white">Your header moment</h2>
                    <p class="mt-2 max-w-xl text-sm text-steel-200">Use a strong cover shot so your profile feels more like a real build page than a plain account card.</p>
                  </div>
                  <label class="inline-flex cursor-pointer items-center justify-center rounded-2xl border border-white/15 bg-black/25 px-4 py-3 text-sm font-semibold text-white transition hover:border-white/30 hover:bg-black/35">
                    <input class="hidden" type="file" accept="image/*" @change="handleCoverUpload" />
                    Upload cover
                  </label>
                </div>
                <div class="flex flex-wrap items-end justify-between gap-4">
                  <div class="space-y-2">
                    <div class="flex items-center gap-2">
                      <p class="text-lg font-semibold text-white">{{ fullName || 'DRVN Member' }}</p>
                      <verified-badge v-if="verified" size="sm" />
                    </div>
                    <p class="text-sm text-steel-200">@{{ handle || 'member' }}</p>
                    <p class="max-w-lg text-sm text-steel-300">{{ pinnedStatus || 'Add a pinned status to give your profile more personality.' }}</p>
                  </div>
                  <div class="flex flex-wrap gap-2 text-xs text-steel-200">
                    <span class="rounded-full border border-white/15 bg-black/20 px-3 py-1.5">{{ countryLabel || 'Global member' }}</span>
                    <span class="rounded-full border border-white/15 bg-black/20 px-3 py-1.5">{{ featuredGarageLabel }}</span>
                  </div>
                </div>
                <div v-if="displayCoverUrl" class="mt-4 rounded-2xl border border-white/10 bg-black/20 p-4">
                  <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p class="text-xs uppercase tracking-[0.28em] text-steel-300">Cover Position</p>
                      <p class="mt-1 text-sm text-steel-300">Move the image up or down until the framing feels right.</p>
                    </div>
                    <div class="flex gap-2">
                      <button type="button" class="rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-white transition hover:border-white/25" @click="setCoverOffsetY(20)">Top</button>
                      <button type="button" class="rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-white transition hover:border-white/25" @click="setCoverOffsetY(50)">Center</button>
                      <button type="button" class="rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-white transition hover:border-white/25" @click="setCoverOffsetY(80)">Bottom</button>
                    </div>
                  </div>
                  <input
                    v-model.number="coverOffsetY"
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    class="mt-4 w-full accent-neon-400"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="rounded-[1.75rem] border border-white/10 bg-[#10141c] p-5 shadow-glow sm:p-6">
            <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p class="text-xs uppercase tracking-[0.32em] text-steel-400">Profile Basics</p>
                <h2 class="mt-2 text-2xl font-semibold text-white">The essentials</h2>
                <p class="mt-2 text-sm text-steel-300">These details shape your name, link, and first impression across DRVN.</p>
              </div>
              <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-xs text-steel-300">
                Public URL
                <p class="mt-1 text-sm font-semibold text-white">{{ publicProfilePath }}</p>
              </div>
            </div>

            <div class="mt-6 grid gap-4 sm:grid-cols-2">
          <label class="block text-sm text-steel-200">
            Display name
            <input
              v-model.trim="fullName"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/70 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Your name"
            />
          </label>

          <label class="block text-sm text-steel-200">
            Username
            <input
              v-model.trim="handle"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/70 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="drvnmember"
            />
            <p class="mt-2 text-xs text-steel-400">Used in your public link and mentions.</p>
          </label>

          <label class="block text-sm text-steel-200">
            Instagram
            <input
              v-model.trim="instagramHandle"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/70 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="yourhandle"
            />
            <p class="mt-2 text-xs text-steel-400">Only your username is needed.</p>
          </label>

          <label class="block text-sm text-steel-200">
            Pinned status
            <input
              v-model.trim="pinnedStatus"
              type="text"
              maxlength="80"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/70 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Available for collabs"
            />
            <p class="mt-2 text-xs text-steel-400">{{ pinnedStatusLength }}/80 characters</p>
          </label>
            </div>
          </div>
          <div class="rounded-[1.75rem] border border-white/10 bg-[#10141c] p-5 shadow-glow sm:p-6">
            <div>
              <p class="text-xs uppercase tracking-[0.32em] text-steel-400">About You</p>
              <h2 class="mt-2 text-2xl font-semibold text-white">Tell people who you are</h2>
              <p class="mt-2 text-sm text-steel-300">Keep this simple and useful so your profile feels like you at a glance.</p>
            </div>

            <div class="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <label for="country" class="text-sm text-steel-200">Country</label>
                <select
                  id="country"
                  name="country"
                  v-model="country"
                  class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/70 px-4 py-3 text-sm text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
                >
                  <option disabled value="">Select a country</option>
                  <option v-for="option in countryOptions" :key="option.code" :value="option.code">
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <label class="block text-sm text-steel-200">
                City
                <input
                  v-model.trim="city"
                  type="text"
                  class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/70 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
                  placeholder="Your city"
                />
              </label>

              <label class="block text-sm text-steel-200 sm:col-span-2">
                Bio
                <textarea
                  v-model.trim="bio"
                  rows="5"
                  class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/70 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
                  placeholder="Tell the community about your build, your drive, and your story."
                ></textarea>
              </label>
            </div>

            <div class="mt-6">
              <div class="flex items-center justify-between gap-3">
                <div>
                  <p class="text-sm font-medium text-steel-200">Interests & tags</p>
                  <p class="mt-1 text-xs text-steel-400">Pick the labels that describe your role in the scene.</p>
                </div>
                <span class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-steel-300">
                  {{ selectedTags.length }} selected
                </span>
              </div>
              <div class="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                <label
                  v-for="tag in availableTags"
                  :key="tag"
                  class="flex cursor-pointer items-center gap-3 rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 transition hover:border-white/20"
                >
                  <input
                    type="checkbox"
                    :value="tag"
                    v-model="selectedTags"
                    class="h-4 w-4 accent-neon-400"
                  />
                  <span class="text-sm text-steel-200">{{ tag }}</span>
                </label>
              </div>
            </div>
          </div>

          <div class="rounded-[1.75rem] border border-white/10 bg-[#10141c] p-5 shadow-glow sm:p-6">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p class="text-xs uppercase tracking-[0.32em] text-steel-400">Links & Spotlight</p>
                <h2 class="mt-2 text-2xl font-semibold text-white">Shape the public profile</h2>
                <p class="mt-2 text-sm text-steel-300">Highlight your best build and connect your other channels without making people hunt for them.</p>
              </div>
              <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-xs text-steel-300">
                Member tier
                <p class="mt-1 text-sm font-semibold capitalize text-white">{{ memberTier }}</p>
              </div>
            </div>

            <div class="mt-6 grid gap-4 sm:grid-cols-2">
              <label class="block text-sm text-steel-200 sm:col-span-2">
                Featured build
                <select
                  v-model="featuredCarId"
                  class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/70 px-4 py-3 text-sm text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
                >
                  <option value="">No featured build</option>
                  <option v-for="car in featuredGarageOptions" :key="car.id" :value="car.id">
                    {{ car.label }}
                  </option>
                </select>
                <p class="mt-2 text-xs text-steel-400">This build gets a dedicated spotlight card on your public profile.</p>
              </label>

              <label class="block text-sm text-steel-200">
                Website
                <input
                  v-model.trim="websiteUrl"
                  type="url"
                  class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/70 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
                  placeholder="https://yourgarage.com"
                />
              </label>

              <label class="block text-sm text-steel-200">
                YouTube
                <input
                  v-model.trim="youtubeUrl"
                  type="url"
                  class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/70 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
                  placeholder="https://youtube.com/@yourbuild"
                />
              </label>

              <label class="block text-sm text-steel-200 sm:col-span-2">
                TikTok
                <input
                  v-model.trim="tiktokUrl"
                  type="url"
                  class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/70 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
                  placeholder="https://tiktok.com/@yourbuild"
                />
              </label>
            </div>
          </div>

          <div class="rounded-[1.75rem] border border-white/10 bg-[#10141c] p-5 shadow-glow sm:p-6">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p class="text-xs uppercase tracking-[0.32em] text-steel-400">Garage</p>
                <h2 class="mt-2 text-2xl font-semibold text-white">Your builds</h2>
                <p class="mt-2 text-sm text-steel-300">Manage the cars that appear on your profile without leaving settings.</p>
              </div>
              <button
                type="button"
                class="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition hover:border-white/30 hover:bg-white/10"
                @click="openGarageEditor()"
              >
                Add car
              </button>
            </div>

            <div v-if="garageCars.length" class="mt-6 space-y-4">
              <div
                v-for="car in garageCars"
                :key="car.id"
                class="rounded-[1.5rem] border border-white/10 bg-midnight-900/55 p-4"
              >
                <div class="flex flex-col gap-4 sm:flex-row sm:items-start">
                  <div class="h-28 w-full overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70 sm:w-40">
                    <img
                      v-if="car.coverImage"
                      :src="car.coverImage"
                      :alt="carDisplayTitle(car)"
                      class="h-full w-full object-cover"
                    />
                    <div v-else class="flex h-full w-full items-center justify-center text-xs uppercase tracking-[0.3em] text-steel-400">
                      No photo
                    </div>
                  </div>

                  <div class="min-w-0 flex-1">
                    <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p class="text-[11px] uppercase tracking-[0.28em] text-steel-400">{{ car.buildStatusLabel }}</p>
                        <h3 class="mt-2 text-xl font-semibold text-white">{{ carDisplayTitle(car) }}</h3>
                        <p class="mt-1 text-sm text-steel-300">{{ car.trim || 'Trim not added yet' }}</p>
                      </div>
                      <span class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] uppercase tracking-[0.24em] text-steel-300">
                        {{ car.year || 'Year TBD' }}
                      </span>
                    </div>

                    <div class="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                        <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Engine</p>
                        <p class="mt-2 text-sm text-white">{{ car.engine || 'Not added yet' }}</p>
                      </div>
                      <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                        <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Drivetrain</p>
                        <p class="mt-2 text-sm text-white">{{ car.drivetrain || 'Not added yet' }}</p>
                      </div>
                      <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                        <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">HP Stock</p>
                        <p class="mt-2 text-sm text-white">{{ car.horsepower_stock || 'N/A' }}</p>
                      </div>
                      <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                        <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">HP Tuned</p>
                        <p class="mt-2 text-sm text-white">{{ car.horsepower_tuned || 'N/A' }}</p>
                      </div>
                    </div>

                    <div class="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                      <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Mods</p>
                      <p class="mt-2 whitespace-pre-line text-sm text-steel-200">{{ car.modifications || 'No modifications added yet.' }}</p>
                    </div>

                    <div class="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4">
                      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Build Journal</p>
                          <p class="mt-2 text-sm text-steel-300">
                            {{ getJournalEntriesForCar(car.id).length ? `${getJournalEntriesForCar(car.id).length} timeline entries logged.` : 'No timeline entries yet.' }}
                          </p>
                        </div>
                        <button
                          type="button"
                          class="rounded-2xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white transition hover:border-white/30 hover:bg-white/10"
                          @click="openJournalEditor(car)"
                        >
                          Add entry
                        </button>
                      </div>

                      <div v-if="getJournalEntriesForCar(car.id).length" class="mt-4 space-y-3">
                        <div
                          v-for="entry in getJournalEntriesForCar(car.id).slice(0, 3)"
                          :key="entry.id"
                          class="rounded-2xl border border-white/10 bg-midnight-900/45 p-4"
                        >
                          <div class="flex flex-col gap-4 sm:flex-row sm:items-start">
                            <div class="min-w-0 flex-1">
                              <div class="flex flex-wrap items-center gap-2">
                                <span class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-steel-300">
                                  {{ entry.entryTypeLabel }}
                                </span>
                                <span class="text-xs text-steel-400">{{ entry.entryDateLabel }}</span>
                                <span v-if="entry.mileageLabel" class="text-xs text-steel-400">{{ entry.mileageLabel }}</span>
                              </div>
                              <h4 class="mt-3 text-base font-semibold text-white">{{ entry.title || 'Untitled update' }}</h4>
                              <p class="mt-2 whitespace-pre-line text-sm text-steel-300">{{ entry.summary || 'No notes added yet.' }}</p>
                            </div>
                            <div
                              v-if="entry.imagePreview"
                              class="h-24 w-full overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70 sm:w-32"
                            >
                              <img
                                :src="entry.imagePreview"
                                :alt="entry.title || 'Build journal entry'"
                                class="h-full w-full object-cover"
                              />
                            </div>
                          </div>
                          <div class="mt-4 flex flex-wrap gap-3">
                            <button
                              type="button"
                              class="rounded-2xl border border-white/15 bg-white/5 px-3 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-white transition hover:border-white/30 hover:bg-white/10"
                              @click="openJournalEditor(car, entry)"
                            >
                              Edit entry
                            </button>
                            <button
                              type="button"
                              class="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-rose-100 transition hover:border-rose-500/70 hover:bg-rose-500/20"
                              @click="confirmJournalDelete(entry)"
                            >
                              Delete entry
                            </button>
                          </div>
                        </div>
                        <p
                          v-if="getJournalEntriesForCar(car.id).length > 3"
                          class="text-xs uppercase tracking-[0.24em] text-steel-400"
                        >
                          Showing the latest 3 entries.
                        </p>
                      </div>
                    </div>

                    <div class="mt-4 flex flex-wrap gap-3">
                      <button
                        type="button"
                        class="rounded-2xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white transition hover:border-white/30 hover:bg-white/10"
                        @click="openGarageEditor(car)"
                      >
                        Edit car
                      </button>
                      <button
                        type="button"
                        class="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-2 text-sm font-semibold text-rose-100 transition hover:border-rose-500/70 hover:bg-rose-500/20"
                        @click="confirmGarageDelete(car)"
                      >
                        Delete car
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="mt-6 rounded-3xl border border-dashed border-white/10 bg-white/[0.03] px-6 py-10 text-center">
              <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Garage Empty</p>
              <p class="mt-3 text-sm text-steel-300">Add your first car so your profile starts to feel like your own showroom.</p>
            </div>
          </div>

          <div class="rounded-[1.75rem] border border-white/10 bg-[#10141c] p-5 shadow-glow sm:p-6">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p class="text-xs uppercase tracking-[0.32em] text-steel-400">Posts</p>
                <h2 class="mt-2 text-2xl font-semibold text-white">Recent posts</h2>
                <p class="mt-2 text-sm text-steel-300">Quick access to recent content in case you want to review or remove something.</p>
              </div>
              <router-link
                to="/feed"
                class="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition hover:border-white/30 hover:bg-white/10"
              >
                Open feed
              </router-link>
            </div>

            <div v-if="myPosts.length" class="mt-6 grid gap-4 md:grid-cols-2">
              <div
                v-for="post in myPosts"
                :key="post.id"
                class="overflow-hidden rounded-[1.5rem] border border-white/10 bg-midnight-900/55"
              >
                <router-link :to="`/post/${post.id}`" class="block">
                  <div class="relative aspect-[4/3] w-full overflow-hidden bg-midnight-900/70">
                    <transition :name="post.mediaDirection === 'prev' ? 'media-swipe-prev' : 'media-swipe-next'" mode="out-in">
                      <img
                        :key="postMediaUrl(post)"
                        :src="postMediaUrl(post)"
                        alt="Post"
                        class="h-full w-full object-cover"
                        :style="{ objectPosition: postMediaPosition(post) }"
                      />
                    </transition>
                    <button
                      v-if="post.mediaUrls?.length > 1"
                      type="button"
                      class="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-black/45 px-2.5 py-1 text-xs text-white"
                      @click.prevent="prevMedia(post)"
                    >
                      Prev
                    </button>
                    <button
                      v-if="post.mediaUrls?.length > 1"
                      type="button"
                      class="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-black/45 px-2.5 py-1 text-xs text-white"
                      @click.prevent="nextMedia(post)"
                    >
                      Next
                    </button>
                  </div>
                  <div class="space-y-2 p-4">
                    <p class="text-base font-semibold text-white">{{ post.caption || 'Untitled post' }}</p>
                    <p v-if="post.description" class="text-sm text-steel-300">{{ post.description }}</p>
                    <div class="flex flex-wrap gap-2 text-xs text-steel-400">
                      <span class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">Likes: {{ post.likeCount }}</span>
                      <span v-if="post.engine" class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">{{ post.engine }}</span>
                      <span v-if="post.year" class="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">{{ post.year }}</span>
                    </div>
                  </div>
                </router-link>
                <div class="px-4 pb-4">
                  <button
                    type="button"
                    class="w-full rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-2 text-sm font-semibold text-rose-100 transition hover:border-rose-500/70 hover:bg-rose-500/20"
                    @click="showDeletePostConfirm = true; postToDelete = post"
                  >
                    Delete post
                  </button>
                </div>
              </div>
            </div>

            <div v-else class="mt-6 rounded-3xl border border-dashed border-white/10 bg-white/[0.03] px-6 py-10 text-center">
              <p class="text-xs uppercase tracking-[0.35em] text-steel-400">No Posts Yet</p>
              <p class="mt-3 text-sm text-steel-300">Your builds will appear here once you publish them from the feed.</p>
            </div>
          </div>
        </div>

        <div class="space-y-6">
          <div class="rounded-[1.75rem] border border-white/10 bg-[#10141c] p-5 shadow-glow sm:p-6">
            <p class="text-xs uppercase tracking-[0.32em] text-steel-400">Account Snapshot</p>
            <div class="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
              <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Member since</p>
                <p class="mt-2 text-sm font-semibold text-white">{{ memberSince }}</p>
              </div>
              <button
                type="button"
                class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left transition hover:border-white/20"
                @click="openFollowModal('followers')"
              >
                <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Followers</p>
                <p class="mt-2 text-sm font-semibold text-white">{{ followerCountLabel }}</p>
              </button>
              <button
                type="button"
                class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left transition hover:border-white/20"
                @click="openFollowModal('following')"
              >
                <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Following</p>
                <p class="mt-2 text-sm font-semibold text-white">{{ followingCountLabel }}</p>
              </button>
              <div class="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">Status</p>
                <p class="mt-2 text-sm font-semibold text-white">{{ statusLabel }}</p>
              </div>
            </div>
          </div>

          <div class="rounded-[1.75rem] border border-white/10 bg-[#10141c] p-5 shadow-glow sm:p-6">
            <p class="text-xs uppercase tracking-[0.32em] text-steel-400">Preview</p>
            <div class="mt-4 rounded-[1.5rem] border border-white/10 bg-midnight-900/70 p-5">
              <p class="text-sm text-steel-400">@{{ sanitizeHandle(handle) || 'member' }}</p>
              <h2 class="mt-2 text-2xl font-semibold text-white">{{ fullName || 'DRVN Member' }}</h2>
              <p class="mt-3 whitespace-pre-line break-words text-sm leading-6 text-steel-300">
                {{ bio || 'Your bio preview will appear here once you add one.' }}
              </p>
              <div class="mt-4 flex flex-wrap gap-2">
                <span
                  v-for="tag in selectedTags"
                  :key="tag"
                  class="rounded-full border border-neon-400/20 bg-neon-400/10 px-3 py-1 text-xs font-medium text-neon-400"
                >
                  {{ tag }}
                </span>
                <span v-if="!selectedTags.length" class="text-xs text-steel-400">No tags selected yet.</span>
              </div>
              <div class="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm text-steel-300">
                <p v-if="city || countryLabel">{{ city }}<span v-if="city && countryLabel">, </span>{{ countryLabel }}</p>
                <p v-if="pinnedStatus" class="text-white">{{ pinnedStatus }}</p>
              </div>
            </div>
          </div>

          <div class="rounded-[1.75rem] border border-rose-500/25 bg-rose-500/[0.06] p-5 shadow-glow sm:p-6">
            <p class="text-xs uppercase tracking-[0.32em] text-rose-200/80">Danger Zone</p>
            <h2 class="mt-2 text-2xl font-semibold text-white">Delete account</h2>
            <p class="mt-2 text-sm leading-6 text-rose-100/80">
              This permanently removes your profile, posts, follow data, and garage entries.
            </p>
            <button
              type="button"
              class="mt-5 w-full rounded-2xl border border-rose-500/45 bg-rose-500/15 px-4 py-3 text-sm font-semibold text-rose-100 transition hover:border-rose-500/70 hover:bg-rose-500/20"
              :disabled="isDeleting"
              @click="showDeleteConfirm = true"
            >
              Delete profile
            </button>
          </div>
        </div>
      </div>

      <div class="hidden z-20 sm:sticky sm:bottom-4 sm:block">
        <div class="rounded-[1.5rem] border border-white/10 bg-[#10141c]/95 p-3 shadow-glow backdrop-blur sm:rounded-[1.75rem] sm:p-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div class="min-w-0">
              <p class="text-sm font-semibold text-white">{{ $t('settings.saveCardTitle') }}</p>
              <p v-if="statusMessage" class="mt-1 text-sm text-steel-300">{{ statusMessage }}</p>
              <p v-else class="mt-1 text-sm text-steel-400">{{ $t('settings.saveCardDescription') }}</p>
            </div>
            <button
              type="button"
              class="w-full rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto sm:px-6"
              :disabled="isSaving"
              @click="saveProfile"
            >
              {{ isSaving ? $t('settings.saving') : $t('settings.saveChanges') }}
            </button>
          </div>
          <p v-if="errorMessage" class="mt-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
            {{ errorMessage }}
          </p>
        </div>
      </div>
    </div>

    <div
      v-if="showDeleteConfirm"
      class="fixed inset-0 z-[140] flex items-center justify-center bg-black/60 px-6"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <h3 class="font-display text-xl font-semibold text-white">Delete profile?</h3>
        <p class="mt-3 text-sm text-steel-300">
          This will remove your profile and posts. This action cannot be undone.
        </p>
        <div class="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
            @click="showDeleteConfirm = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-2xl bg-rose-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-rose-400"
            :disabled="isDeleting"
            @click="deleteProfile"
          >
            {{ isDeleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showDeletePostConfirm"
      class="fixed inset-0 z-[140] flex items-center justify-center bg-black/60 px-6"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <h3 class="font-display text-xl font-semibold text-white">Delete post?</h3>
        <p class="mt-3 text-sm text-steel-300">
          This will permanently remove the post. This action cannot be undone.
        </p>
        <div class="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
            @click="showDeletePostConfirm = false; postToDelete = null"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-2xl bg-rose-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-rose-400"
            @click="confirmDeletePost"
          >
            Delete
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showGarageModal"
      class="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 px-6"
      @click.self="closeGarageEditor"
    >
      <div class="w-full max-w-3xl rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow overflow-auto max-h-[90vh]">
        <div class="flex items-center justify-between gap-4">
          <div>
            <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Garage Editor</p>
            <h3 class="mt-2 font-display text-xl font-semibold text-white">{{ garageForm.id ? 'Edit car' : 'Add a car' }}</h3>
          </div>
          <button
            type="button"
            class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
            @click="closeGarageEditor"
          >
            Close
          </button>
        </div>

        <div class="mt-5 grid gap-4 sm:grid-cols-2">
          <label class="block text-sm text-steel-200">
            Make
            <input
              v-model.trim="garageForm.make"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="BMW"
            />
          </label>
          <label class="block text-sm text-steel-200">
            Model
            <input
              v-model.trim="garageForm.model"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="M4"
            />
          </label>
          <label class="block text-sm text-steel-200">
            Year
            <input
              v-model.number="garageForm.year"
              type="number"
              min="1900"
              max="2100"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="2022"
            />
          </label>
          <label class="block text-sm text-steel-200">
            Trim
            <input
              v-model.trim="garageForm.trim"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Competition xDrive"
            />
          </label>
          <label class="block text-sm text-steel-200">
            Engine
            <input
              v-model.trim="garageForm.engine"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="3.0L twin-turbo inline-6"
            />
          </label>
          <label class="block text-sm text-steel-200">
            Drivetrain
            <input
              v-model.trim="garageForm.drivetrain"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="AWD / RWD"
            />
          </label>
          <label class="block text-sm text-steel-200">
            HP Stock
            <input
              v-model.number="garageForm.horsepower_stock"
              type="number"
              min="0"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="503"
            />
          </label>
          <label class="block text-sm text-steel-200">
            HP Tuned
            <input
              v-model.number="garageForm.horsepower_tuned"
              type="number"
              min="0"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="710"
            />
          </label>
          <label class="block text-sm text-steel-200 sm:col-span-2">
            Build status
            <select
              v-model="garageForm.build_status"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            >
              <option v-for="option in garageStatusOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="block text-sm text-steel-200 sm:col-span-2">
            Mods
            <textarea
              v-model.trim="garageForm.modifications"
              rows="4"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Downpipes, tune, forged wheels, carbon intake..."
            ></textarea>
          </label>
          <div class="sm:col-span-2 rounded-2xl border border-white/10 bg-midnight-900/60 p-4">
            <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p class="text-sm text-white">Garage photo</p>
                <p class="mt-1 text-xs text-steel-400">Uses your existing `posts` storage bucket, so there is no extra bucket setup.</p>
              </div>
              <label class="inline-flex cursor-pointer items-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:border-white/30 hover:bg-white/10">
                <input class="hidden" type="file" accept="image/*" @change="handleGarageImageUpload" />
                Upload photo
              </label>
            </div>
            <div class="mt-4 aspect-[4/3] overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70">
              <img
                v-if="garageForm.coverImage"
                :src="garageForm.coverImage"
                alt="Garage preview"
                class="h-full w-full object-cover"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-xs uppercase tracking-[0.3em] text-steel-400">
                No image uploaded
              </div>
            </div>
          </div>
        </div>

        <div class="mt-6 flex flex-wrap items-center justify-end gap-3">
          <p v-if="garageStatusMessage" class="mr-auto text-sm text-steel-300">{{ garageStatusMessage }}</p>
          <p v-if="garageErrorMessage" class="mr-auto text-sm text-rose-200">{{ garageErrorMessage }}</p>
          <button
            type="button"
            class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
            @click="closeGarageEditor"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-2xl bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="garageSaving"
            @click="saveGarageCar"
          >
            {{ garageSaving ? 'Saving...' : garageForm.id ? 'Update car' : 'Save car' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showJournalModal"
      class="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 px-6"
      @click.self="closeJournalEditor"
    >
      <div class="w-full max-w-2xl rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow overflow-auto max-h-[90vh]">
        <div class="flex items-center justify-between gap-4">
          <div>
            <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Build Journal</p>
            <h3 class="mt-2 font-display text-xl font-semibold text-white">{{ journalForm.id ? 'Edit entry' : 'Add entry' }}</h3>
            <p class="mt-2 text-sm text-steel-300">{{ selectedJournalCarLabel }}</p>
          </div>
          <button
            type="button"
            class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
            @click="closeJournalEditor"
          >
            Close
          </button>
        </div>

        <div class="mt-5 grid gap-4 sm:grid-cols-2">
          <label class="block text-sm text-steel-200 sm:col-span-2">
            Title
            <input
              v-model.trim="journalForm.title"
              type="text"
              maxlength="80"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Installed coilovers and dialed ride height"
            />
          </label>
          <label class="block text-sm text-steel-200">
            Entry type
            <select
              v-model="journalForm.entry_type"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            >
              <option v-for="option in journalEntryTypeOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="block text-sm text-steel-200">
            Date
            <input
              v-model="journalForm.entry_date"
              type="date"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            />
          </label>
          <label class="block text-sm text-steel-200 sm:col-span-2">
            Mileage
            <input
              v-model.number="journalForm.mileage"
              type="number"
              min="0"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="58200"
            />
            <p class="mt-2 text-xs text-steel-400">Optional. Good for service logs, upgrades, and progress milestones.</p>
          </label>
          <label class="block text-sm text-steel-200 sm:col-span-2">
            Summary
            <textarea
              v-model.trim="journalForm.summary"
              rows="5"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="What changed, what parts went on, what still needs work, and how the car feels now."
            ></textarea>
          </label>
          <div class="sm:col-span-2 rounded-2xl border border-white/10 bg-midnight-900/60 p-4">
            <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p class="text-sm text-white">Journal photo</p>
                <p class="mt-1 text-xs text-steel-400">Optional progress shot stored in the existing `posts` bucket.</p>
              </div>
              <label class="inline-flex cursor-pointer items-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:border-white/30 hover:bg-white/10">
                <input class="hidden" type="file" accept="image/*" @change="handleJournalImageUpload" />
                Upload photo
              </label>
            </div>
            <div class="mt-4 aspect-[4/3] overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70">
              <img
                v-if="journalForm.imagePreview"
                :src="journalForm.imagePreview"
                alt="Build journal preview"
                class="h-full w-full object-cover"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-xs uppercase tracking-[0.3em] text-steel-400">
                No image uploaded
              </div>
            </div>
          </div>
        </div>

        <div class="mt-6 flex flex-wrap items-center justify-end gap-3">
          <p v-if="journalStatusMessage" class="mr-auto text-sm text-steel-300">{{ journalStatusMessage }}</p>
          <p v-if="journalErrorMessage" class="mr-auto text-sm text-rose-200">{{ journalErrorMessage }}</p>
          <button
            type="button"
            class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
            @click="closeJournalEditor"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-2xl bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="journalSaving"
            @click="saveJournalEntry"
          >
            {{ journalSaving ? 'Saving...' : journalForm.id ? 'Update entry' : 'Save entry' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showDeleteGarageConfirm"
      class="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 px-6"
      @click.self="cancelGarageDelete"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <h3 class="font-display text-xl font-semibold text-white">Delete car?</h3>
        <p class="mt-3 text-sm text-steel-300">This will remove the car from your garage showcase.</p>
        <div class="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
            @click="cancelGarageDelete"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-2xl bg-rose-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-rose-400 disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="garageDeleting"
            @click="deleteGarageCar"
          >
            {{ garageDeleting ? 'Deleting...' : 'Delete car' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showDeleteJournalConfirm"
      class="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 px-6"
      @click.self="cancelJournalDelete"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <h3 class="font-display text-xl font-semibold text-white">Delete journal entry?</h3>
        <p class="mt-3 text-sm text-steel-300">This removes the timeline update from the build journal.</p>
        <div class="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
            @click="cancelJournalDelete"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-2xl bg-rose-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-rose-400 disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="journalDeleting"
            @click="deleteJournalEntry"
          >
            {{ journalDeleting ? 'Deleting...' : 'Delete entry' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showFollowModal"
      class="fixed inset-0 z-[140] overflow-y-auto bg-black/70 px-4 py-6 sm:flex sm:items-center sm:justify-center sm:px-6"
      @click.self="closeFollowModal"
    >
      <div class="follow-modal-panel my-auto flex w-full max-w-md flex-col rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <div class="flex items-center justify-between gap-3">
          <div>
            <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Connections</p>
            <h3 class="mt-2 font-display text-xl font-semibold text-white">
              {{ followModalType === 'followers' ? 'Followers' : 'Following' }}
            </h3>
          </div>
          <button
            type="button"
            class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
            @click="closeFollowModal"
          >
            Close
          </button>
        </div>

        <p v-if="followModalLoading" class="mt-4 text-sm text-steel-300">Loading...</p>
        <p v-else-if="followModalError" class="mt-4 text-sm text-rose-200">{{ followModalError }}</p>
        <p v-else-if="!followModalUsers.length" class="mt-4 text-sm text-steel-300">
          {{ followModalType === 'followers' ? 'No followers yet.' : 'Not following anyone yet.' }}
        </p>
        <div v-else class="follow-modal-list mt-4 space-y-3 overflow-y-auto pr-1">
          <router-link
            v-for="user in followModalUsers"
            :key="`${followModalType}-${user.id}`"
            :to="`/u/${user.handle}`"
            class="flex items-center gap-3 rounded-2xl border border-white/10 bg-midnight-900/60 px-3 py-2 transition hover:border-white/20"
            @click="closeFollowModal"
          >
            <div class="h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
              <img
                v-if="user.avatar"
                :src="user.avatar"
                alt=""
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">
                {{ user.initials }}
              </div>
            </div>
            <div class="min-w-0">
              <div class="flex items-center gap-1.5">
                <p class="truncate text-sm text-white">{{ user.fullName }}</p>
                <verified-badge v-if="user.verified" size="xs" />
              </div>
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ user.handle }}</p>
            </div>
          </router-link>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { supabase, getAvatarUrl, getPostImageUrl, refreshReadySession, getStoragePublicUrl } from '@/lib/supabase'
import { getLocaleOptions, setLocale } from '@/lib/i18n'
import { emitAppToast } from '@/lib/toast'
import VerifiedBadge from '@/components/VerifiedBadge.vue'

export default {
  name: 'SettingsView',
  components: {
    VerifiedBadge,
  },
  data() {
    return {
      email: '',
      fullName: '',
      bio: '',
      avatarUrl: '',
      avatarPath: '',
      avatarPreviewUrl: '',
      coverUrl: '',
      coverPath: '',
      coverPreviewUrl: '',
      coverOffsetY: 50,
      role: '',
      verified: false,
      memberTier: 'member',
      championsLeagueWinner: false,
      status: '',
      memberSince: '',
      isSaving: false,
      statusMessage: '',
      errorMessage: '',
      handle: '',
      instagramHandle: '',
      websiteUrl: '',
      youtubeUrl: '',
      tiktokUrl: '',
      pinnedStatus: '',
      country: '',
      city: '',
      selectedTags: [],
      featuredCarId: '',
      availableTags: [
        'Car Enthusiast',
        'Photographer',
        'Mechanic',
        'Customizer',
        'Collector',
        'Racer',
        'Builder',
        'Tuner',
      ],
      countryOptions: [
        { code: 'US', label: 'USA', flag: '🇺🇸' },
        { code: 'GB', label: 'UK', flag: '🇬🇧' },
        { code: 'CA', label: 'Canada', flag: '🇨🇦' },
        { code: 'JP', label: 'Japan', flag: '🇯🇵' },
        { code: 'KR', label: 'Korea', flag: '🇰🇷' },
        { code: 'BR', label: 'Brazil', flag: '🇧🇷' },
        { code: 'AL', label: 'Albania', flag: '🇦🇱' },
        { code: 'AD', label: 'Andorra', flag: '🇦🇩' },
        { code: 'AM', label: 'Armenia', flag: '🇦🇲' },
        { code: 'AT', label: 'Austria', flag: '🇦🇹' },
        { code: 'AZ', label: 'Azerbaijan', flag: '🇦🇿' },
        { code: 'BY', label: 'Belarus', flag: '🇧🇾' },
        { code: 'BE', label: 'Belgium', flag: '🇧🇪' },
        { code: 'BA', label: 'Bosnia and Herzegovina', flag: '🇧🇦' },
        { code: 'BG', label: 'Bulgaria', flag: '🇧🇬' },
        { code: 'HR', label: 'Croatia', flag: '🇭🇷' },
        { code: 'CY', label: 'Cyprus', flag: '🇨🇾' },
        { code: 'CZ', label: 'Czechia', flag: '🇨🇿' },
        { code: 'DK', label: 'Denmark', flag: '🇩🇰' },
        { code: 'EE', label: 'Estonia', flag: '🇪🇪' },
        { code: 'FI', label: 'Finland', flag: '🇫🇮' },
        { code: 'FR', label: 'France', flag: '🇫🇷' },
        { code: 'GE', label: 'Georgia', flag: '🇬🇪' },
        { code: 'DE', label: 'Germany', flag: '🇩🇪' },
        { code: 'GR', label: 'Greece', flag: '🇬🇷' },
        { code: 'HU', label: 'Hungary', flag: '🇭🇺' },
        { code: 'IS', label: 'Iceland', flag: '🇮🇸' },
        { code: 'IE', label: 'Ireland', flag: '🇮🇪' },
        { code: 'IT', label: 'Italy', flag: '🇮🇹' },
        { code: 'KZ', label: 'Kazakhstan', flag: '🇰🇿' },
        { code: 'XK', label: 'Kosovo', flag: '🇽🇰' },
        { code: 'LV', label: 'Latvia', flag: '🇱🇻' },
        { code: 'LI', label: 'Liechtenstein', flag: '🇱🇮' },
        { code: 'LT', label: 'Lithuania', flag: '🇱🇹' },
        { code: 'LU', label: 'Luxembourg', flag: '🇱🇺' },
        { code: 'MT', label: 'Malta', flag: '🇲🇹' },
        { code: 'MD', label: 'Moldova', flag: '🇲🇩' },
        { code: 'MC', label: 'Monaco', flag: '🇲🇨' },
        { code: 'ME', label: 'Montenegro', flag: '🇲🇪' },
        { code: 'NL', label: 'Netherlands', flag: '🇳🇱' },
        { code: 'MK', label: 'North Macedonia', flag: '🇲🇰' },
        { code: 'NO', label: 'Norway', flag: '🇳🇴' },
        { code: 'PL', label: 'Poland', flag: '🇵🇱' },
        { code: 'PT', label: 'Portugal', flag: '🇵🇹' },
        { code: 'RO', label: 'Romania', flag: '🇷🇴' },
        { code: 'RU', label: 'Russia', flag: '🇷🇺' },
        { code: 'SM', label: 'San Marino', flag: '🇸🇲' },
        { code: 'RS', label: 'Serbia', flag: '🇷🇸' },
        { code: 'SK', label: 'Slovakia', flag: '🇸🇰' },
        { code: 'SI', label: 'Slovenia', flag: '🇸🇮' },
        { code: 'ES', label: 'Spain', flag: '🇪🇸' },
        { code: 'SE', label: 'Sweden', flag: '🇸🇪' },
        { code: 'CH', label: 'Switzerland', flag: '🇨🇭' },
        { code: 'TR', label: 'Turkey', flag: '🇹🇷' },
        { code: 'UA', label: 'Ukraine', flag: '🇺🇦' },
        { code: 'GB', label: 'United Kingdom', flag: '🇬🇧' },
        { code: 'VA', label: 'Vatican City', flag: '🇻🇦' },
      ],
      myPosts: [],
      showDeleteConfirm: false,
      showDeletePostConfirm: false,
      postToDelete: null,
      isDeleting: false,
      followerCount: 0,
      followingCount: 0,
      garageCars: [],
      buildJournalEntriesByCar: {},
      showGarageModal: false,
      garageSaving: false,
      garageDeleting: false,
      garageErrorMessage: '',
      garageStatusMessage: '',
      showDeleteGarageConfirm: false,
      garageCarToDelete: null,
      showJournalModal: false,
      journalSaving: false,
      journalDeleting: false,
      journalErrorMessage: '',
      journalStatusMessage: '',
      showDeleteJournalConfirm: false,
      journalEntryToDelete: null,
      showFollowModal: false,
      followModalType: 'followers',
      followModalUsers: [],
      followModalLoading: false,
      followModalError: '',
      visibilityHandler: null,
      isRefreshingView: false,
      garageStatusOptions: [
        { value: 'building', label: 'Building' },
        { value: 'street', label: 'Street' },
        { value: 'track', label: 'Track' },
        { value: 'show', label: 'Show' },
        { value: 'sale', label: 'For Sale' },
      ],
      journalEntryTypeOptions: [
        { value: 'update', label: 'Update' },
        { value: 'mod', label: 'Modification' },
        { value: 'maintenance', label: 'Maintenance' },
        { value: 'milestone', label: 'Milestone' },
      ],
      garageForm: {
        id: '',
        make: '',
        model: '',
        year: null,
        trim: '',
        engine: '',
        drivetrain: '',
        horsepower_stock: null,
        horsepower_tuned: null,
        modifications: '',
        build_status: 'building',
        cover_image_url: '',
        cover_image_path: '',
        coverImage: '',
      },
      journalForm: {
        id: '',
        garage_car_id: '',
        title: '',
        summary: '',
        entry_type: 'update',
        entry_date: '',
        mileage: null,
        image_url: '',
        image_path: '',
        imagePreview: '',
      },
    }
  },
  computed: {
    statusLabel() {
      return this.status === 'banned' ? 'Banned' : 'Active'
    },
    roleLabel() {
      return (this.role || 'member')
    },
    roleBadgeClass() {
      const role = (this.role || 'member').toLowerCase()
      if (role === 'admin') return 'bg-rose-500/10 border-rose-500/30 text-rose-200'
      if (role === 'moderator') return 'bg-violet-400/10 border-violet-400/30 text-violet-200'
      return 'bg-neon-400/10 border-neon-400/20 text-neon-400'
    },
    championBadgeClass() {
      return this.championsLeagueWinner
        ? 'bg-amber-300/20 border-amber-300/50 text-amber-100'
        : 'bg-white/5 border-white/10 text-steel-400'
    },
    verifiedBadgeClass() {
      return this.verified
        ? 'bg-sky-400/12 border-sky-400/40 text-sky-300'
        : 'bg-white/5 border-white/10 text-steel-400'
    },
    initials() {
      if (!this.fullName) return 'D'
      return this.fullName
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((chunk) => chunk[0].toUpperCase())
        .join('')
    },
    displayAvatarUrl() {
      if (this.avatarPreviewUrl) return this.avatarPreviewUrl
      return getAvatarUrl({ avatarPath: this.avatarPath, avatarUrl: this.avatarUrl })
    },
    displayCoverUrl() {
      if (this.coverPreviewUrl) return this.coverPreviewUrl
      return getStoragePublicUrl({
        bucket: 'avatars',
        path: this.coverPath,
        fallbackUrl: this.coverUrl,
      })
    },
    coverObjectPosition() {
      return `50% ${this.coverOffsetY}%`
    },
    publicProfilePath() {
      return `/u/${this.sanitizeHandle(this.handle) || 'member'}`
    },
    countryLabel() {
      return this.countryOptions.find((option) => option.code === this.country)?.label || this.country
    },
    pinnedStatusLength() {
      return (this.pinnedStatus || '').length
    },
    followerCountLabel() {
      return Number(this.followerCount || 0).toLocaleString()
    },
    followingCountLabel() {
      return Number(this.followingCount || 0).toLocaleString()
    },
    featuredGarageOptions() {
      return this.garageCars.map((car) => ({
        id: car.id,
        label: this.carDisplayTitle(car),
      }))
    },
    featuredGarageLabel() {
      return this.garageCars.find((car) => car.id === this.featuredCarId)?.buildStatusLabel || 'No featured build yet'
    },
    selectedJournalCarLabel() {
      const car = this.garageCars.find((item) => item.id === this.journalForm.garage_car_id)
      return car ? this.carDisplayTitle(car) : 'Select a build'
    },
    currentLocale() {
      return this.$i18n.locale
    },
    languageOptions() {
      return getLocaleOptions()
    },
  },
  async mounted() {
    await this.refreshViewData()

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.refreshViewData()
      }
    }
    document.addEventListener('visibilitychange', this.visibilityHandler)
  },
  beforeUnmount() {
    this.revokeAvatarPreviewUrl()
    this.revokeCoverPreviewUrl()
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
      this.visibilityHandler = null
    }
  },
  methods: {
    handleLocaleChange(event) {
      setLocale(event?.target?.value)
    },
    async refreshViewData() {
      if (this.isRefreshingView) return
      this.isRefreshingView = true

      try {
        const session = await refreshReadySession()
        if (!session?.user) {
          this.$router.push('/login')
          return
        }

        const user = session.user
        this.email = user.email || ''
        this.memberSince = new Date(user.created_at).toLocaleDateString()

        const { data: profile } = await supabase
          .from('profiles')
          .select('handle, full_name, bio, avatar_url, avatar_path, cover_url, cover_path, cover_offset_y, featured_car_id, role, member_tier, verified, champions_league_winner, status, country, city, instagram_handle, website_url, youtube_url, tiktok_url, tags, pinned_status')
          .eq('id', user.id)
          .maybeSingle()

        this.fullName = profile?.full_name || user.user_metadata?.full_name || ''
        this.bio = profile?.bio || user.user_metadata?.bio || ''
        this.avatarUrl = profile?.avatar_url || user.user_metadata?.avatar_url || ''
        this.avatarPath = profile?.avatar_path || ''
        this.coverUrl = profile?.cover_url || ''
        this.coverPath = profile?.cover_path || ''
        this.coverOffsetY = Number.isFinite(profile?.cover_offset_y) ? profile.cover_offset_y : 50
        this.handle = profile?.handle || user.user_metadata?.handle || ''
        this.instagramHandle = profile?.instagram_handle || ''
        this.websiteUrl = profile?.website_url || ''
        this.youtubeUrl = profile?.youtube_url || ''
        this.tiktokUrl = profile?.tiktok_url || ''
        this.pinnedStatus = profile?.pinned_status || ''
        this.role = profile?.role || 'member'
        this.memberTier = profile?.member_tier || 'member'
        this.verified = Boolean(profile?.verified)
        this.featuredCarId = profile?.featured_car_id || ''
        this.championsLeagueWinner = Boolean(profile?.champions_league_winner)
        this.status = profile?.status || 'active'
        this.country = profile?.country || ''
        this.city = profile?.city || ''
        this.selectedTags = profile?.tags || []

        await this.fetchMyPosts(user.id)
        await this.fetchFollowCounts(user.id)
        await this.fetchGarageCars(user.id)
        await this.fetchBuildJournalEntries(user.id)
      } finally {
        this.isRefreshingView = false
      }
    },
    sanitizeHandle(value) {
      return (value || '')
        .toLowerCase()
        .replace(/^@+/, '')
        .replace(/[^a-z0-9._-]/g, '')
        .slice(0, 30)
    },
    sanitizeInstagramHandle(value) {
      return (value || '')
        .trim()
        .replace(/^https?:\/\/(www\.)?instagram\.com\//i, '')
        .replace(/^@+/, '')
        .replace(/\/.*$/, '')
        .replace(/[^a-zA-Z0-9._]/g, '')
        .slice(0, 30)
    },
    sanitizeLink(value) {
      const trimmed = String(value || '').trim()
      if (!trimmed) return ''
      return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    },
    setCoverOffsetY(value) {
      const nextValue = Number(value)
      this.coverOffsetY = Number.isFinite(nextValue) ? Math.min(100, Math.max(0, nextValue)) : 50
    },
    async upsertProfilePatch(userId, patch = {}) {
      const fallbackHandle = this.sanitizeHandle(this.handle) || userId.slice(0, 8)
      return supabase
        .from('profiles')
        .upsert(
          {
            id: userId,
            handle: fallbackHandle,
            ...patch,
          },
          { onConflict: 'id' }
        )
    },
    revokeAvatarPreviewUrl() {
      if (!this.avatarPreviewUrl) return
      URL.revokeObjectURL(this.avatarPreviewUrl)
      this.avatarPreviewUrl = ''
    },
    revokeCoverPreviewUrl() {
      if (!this.coverPreviewUrl) return
      URL.revokeObjectURL(this.coverPreviewUrl)
      this.coverPreviewUrl = ''
    },
    resetGarageForm() {
      this.garageForm = {
        id: '',
        make: '',
        model: '',
        year: null,
        trim: '',
        engine: '',
        drivetrain: '',
        horsepower_stock: null,
        horsepower_tuned: null,
        modifications: '',
        build_status: 'building',
        cover_image_url: '',
        cover_image_path: '',
        coverImage: '',
      }
    },
    resetJournalForm() {
      this.journalForm = {
        id: '',
        garage_car_id: '',
        title: '',
        summary: '',
        entry_type: 'update',
        entry_date: new Date().toISOString().slice(0, 10),
        mileage: null,
        image_url: '',
        image_path: '',
        imagePreview: '',
      }
    },
    buildGarageImageUrl({ cover_image_path, cover_image_url }) {
      if (cover_image_path) {
        return getPostImageUrl({ imagePath: cover_image_path, imageUrl: cover_image_url })
      }
      return cover_image_url || ''
    },
    buildJournalImageUrl({ image_path, image_url }) {
      if (image_path) {
        return getPostImageUrl({ imagePath: image_path, imageUrl: image_url })
      }
      return image_url || ''
    },
    buildStatusLabel(value) {
      const map = {
        building: 'Building',
        street: 'Street',
        track: 'Track',
        show: 'Show',
        sale: 'For Sale',
      }
      return map[value] || 'Build'
    },
    journalEntryTypeLabel(value) {
      const map = {
        update: 'Update',
        mod: 'Modification',
        maintenance: 'Maintenance',
        milestone: 'Milestone',
      }
      return map[value] || 'Update'
    },
    formatJournalDate(value) {
      if (!value) return 'Date TBD'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return 'Date TBD'
      return parsed.toLocaleDateString()
    },
    formatJournalMileage(value) {
      if (!Number.isFinite(value)) return ''
      return `${Number(value).toLocaleString()} mi`
    },
    carDisplayTitle(car) {
      return [car.make, car.model].filter(Boolean).join(' ') || 'Untitled build'
    },
    normalizeJournalEntry(entry) {
      return {
        ...entry,
        imagePreview: this.buildJournalImageUrl(entry),
        entryTypeLabel: this.journalEntryTypeLabel(entry.entry_type),
        entryDateLabel: this.formatJournalDate(entry.entry_date),
        mileageLabel: this.formatJournalMileage(entry.mileage),
      }
    },
    groupJournalEntries(entries) {
      return (entries || []).reduce((accumulator, entry) => {
        const key = entry.garage_car_id
        if (!key) return accumulator
        if (!accumulator[key]) {
          accumulator[key] = []
        }
        accumulator[key].push(this.normalizeJournalEntry(entry))
        return accumulator
      }, {})
    },
    getJournalEntriesForCar(carId) {
      return this.buildJournalEntriesByCar[carId] || []
    },
    postMediaUrl(post) {
      return post.mediaUrls?.[post.mediaIndex] || post.imageUrl || ''
    },
    postMediaPosition(post) {
      const offset = post.mediaOffsets?.[post.mediaIndex] || post.mediaOffsets?.[0] || { x: 50, y: 50 }
      return `${offset.x}% ${offset.y}%`
    },
    async convertImageToWebp(file, { maxWidth, quality = 0.76, maxPixels = 1600000 }) {
      const imageObjectUrl = URL.createObjectURL(file)
      const image = await new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error('Image decode failed.'))
        img.src = imageObjectUrl
      })

      try {
        const originalWidth = image.width || 1
        const originalHeight = image.height || 1
        const widthScale = Math.min(1, maxWidth / originalWidth)
        const pixelScale = Math.min(1, Math.sqrt(maxPixels / (originalWidth * originalHeight || 1)))
        const scale = Math.min(widthScale, pixelScale)
        const targetWidth = Math.max(1, Math.round(originalWidth * scale))
        const targetHeight = Math.max(1, Math.round(originalHeight * scale))

        const canvas = document.createElement('canvas')
        canvas.width = targetWidth
        canvas.height = targetHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          throw new Error('Canvas is not available.')
        }
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.drawImage(image, 0, 0, targetWidth, targetHeight)

        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', quality))
        if (!blob) throw new Error('Image conversion failed.')

        return new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), { type: 'image/webp' })
      } finally {
        URL.revokeObjectURL(imageObjectUrl)
      }
    },
    async uploadProfileAsset(file, { prefix, maxWidth }) {
      const { data } = await supabase.auth.getSession()
      const userId = data?.session?.user?.id

      if (!userId) {
        throw new Error('Please sign in again to upload an image.')
      }

      const uploadFile = await this.convertImageToWebp(file, {
        maxWidth,
        quality: prefix === 'avatar' ? 0.8 : 0.74,
        maxPixels: prefix === 'avatar' ? 250000 : 1400000,
      })
      const filePath = `${userId}/${prefix}-${Date.now()}.webp`

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, uploadFile, { upsert: false, contentType: 'image/webp', cacheControl: '31536000' })

      if (uploadError) {
        throw new Error('Upload failed. Make sure the "avatars" bucket exists.')
      }

      return {
        filePath,
        publicUrl: getStoragePublicUrl({
          bucket: 'avatars',
          path: filePath,
        }),
      }
    },
    async fetchMyPosts(userId) {
      const { data, error } = await supabase
        .from('posts')
        .select('id, caption, description, modifications, engine, hp_stock, hp_tuned, year, image_url, image_path, image_offset_x, image_offset_y, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (error) return

      const posts = (data || []).map((post) => ({
        ...post,
        imageUrl: post.image_path
          ? getPostImageUrl({ imagePath: post.image_path, imageUrl: post.image_url })
          : post.image_url,
        mediaUrls: post.image_path
          ? [getPostImageUrl({ imagePath: post.image_path, imageUrl: post.image_url })]
          : post.image_url
            ? [post.image_url]
            : [],
        mediaOffsets: [
          {
            x: Number.isFinite(post.image_offset_x) ? post.image_offset_x : 50,
            y: Number.isFinite(post.image_offset_y) ? post.image_offset_y : 50,
          },
        ],
        mediaIndex: 0,
        mediaDirection: 'next',
        likeCount: 0,
      }))

      const postIds = posts.map((post) => post.id)
      if (postIds.length) {
        const { data: media } = await supabase
          .from('post_media')
          .select('post_id, image_url, image_path, sort_order, offset_x, offset_y')
          .in('post_id', postIds)
          .order('sort_order', { ascending: true })

        posts.forEach((post) => {
          const items = (media || []).filter((item) => item.post_id === post.id)
          const urls = items.map((item) =>
            item.image_path
              ? getPostImageUrl({ imagePath: item.image_path, imageUrl: item.image_url })
              : item.image_url
          )
          post.mediaUrls = [
            ...(post.mediaUrls || []).filter(Boolean),
            ...urls.filter(Boolean),
          ]
          post.mediaOffsets = [
            post.mediaOffsets?.[0] || { x: 50, y: 50 },
            ...items.map((item) => ({
              x: Number.isFinite(item.offset_x) ? item.offset_x : 50,
              y: Number.isFinite(item.offset_y) ? item.offset_y : 50,
            })),
          ]
        })

        const { data: likes } = await supabase
          .from('post_likes')
          .select('post_id')
          .in('post_id', postIds)

        const likeCounts = new Map()
        ;(likes || []).forEach((like) => {
          likeCounts.set(like.post_id, (likeCounts.get(like.post_id) || 0) + 1)
        })
        posts.forEach((post) => {
          post.likeCount = likeCounts.get(post.id) || 0
        })
      }

      this.myPosts = posts
    },
    async fetchGarageCars(userId) {
      const { data, error } = await supabase
        .from('garage_cars')
        .select('id, make, model, year, trim, engine, drivetrain, horsepower_stock, horsepower_tuned, modifications, build_status, cover_image_url, cover_image_path, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (error) {
        this.garageCars = []
        return
      }

      this.garageCars = (data || []).map((car) => ({
        ...car,
        coverImage: this.buildGarageImageUrl(car),
        buildStatusLabel: this.buildStatusLabel(car.build_status),
      }))
    },
    async fetchBuildJournalEntries(userId) {
      const { data, error } = await supabase
        .from('build_journal_entries')
        .select('id, garage_car_id, title, summary, entry_type, entry_date, mileage, image_url, image_path, created_at')
        .eq('user_id', userId)
        .order('entry_date', { ascending: false })
        .order('created_at', { ascending: false })

      if (error) {
        this.buildJournalEntriesByCar = {}
        return
      }

      this.buildJournalEntriesByCar = this.groupJournalEntries(data || [])
    },
    async fetchFollowCounts(userId) {
      const [followersResult, followingResult] = await Promise.all([
        supabase
          .from('user_follows')
          .select('id', { count: 'exact', head: true })
          .eq('following_id', userId),
        supabase
          .from('user_follows')
          .select('id', { count: 'exact', head: true })
          .eq('follower_id', userId),
      ])

      if (followersResult.error || followingResult.error) {
        this.followerCount = 0
        this.followingCount = 0
        return
      }

      this.followerCount = followersResult.count || 0
      this.followingCount = followingResult.count || 0
    },
    formatFollowUsers(rows, key) {
      return (rows || []).map((row) => {
        const profile = row[key] || {}
        const fullName = profile.full_name || 'DRVN Member'
        return {
          id: profile.id || row.id,
          handle: profile.handle || 'member',
          fullName,
          avatar: getAvatarUrl({ avatarPath: profile.avatar_path, avatarUrl: profile.avatar_url }),
          verified: Boolean(profile.verified),
          initials: fullName
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((chunk) => chunk[0].toUpperCase())
            .join(''),
        }
      })
    },
    async openFollowModal(type) {
      const { data: sessionData } = await supabase.auth.getSession()
      const userId = sessionData?.session?.user?.id
      if (!userId) return

      this.showFollowModal = true
      this.followModalType = type
      this.followModalUsers = []
      this.followModalError = ''
      this.followModalLoading = true

      const isFollowers = type === 'followers'
      const { data, error } = await supabase
        .from('user_follows')
        .select(
          isFollowers
            ? 'id, follower:profiles!user_follows_follower_id_fkey (id, handle, full_name, avatar_url, avatar_path, verified)'
            : 'id, following:profiles!user_follows_following_id_fkey (id, handle, full_name, avatar_url, avatar_path, verified)'
        )
        .eq(isFollowers ? 'following_id' : 'follower_id', userId)
        .order('created_at', { ascending: false })

      this.followModalLoading = false

      if (error) {
        this.followModalError = 'Unable to load this list right now.'
        return
      }

      this.followModalUsers = this.formatFollowUsers(data, isFollowers ? 'follower' : 'following')
    },
    closeFollowModal() {
      this.showFollowModal = false
      this.followModalUsers = []
      this.followModalError = ''
      this.followModalLoading = false
    },
    openGarageEditor(car = null) {
      this.garageErrorMessage = ''
      this.garageStatusMessage = ''
      if (!car) {
        this.resetGarageForm()
      } else {
        this.garageForm = {
          id: car.id,
          make: car.make || '',
          model: car.model || '',
          year: car.year || null,
          trim: car.trim || '',
          engine: car.engine || '',
          drivetrain: car.drivetrain || '',
          horsepower_stock: car.horsepower_stock || null,
          horsepower_tuned: car.horsepower_tuned || null,
          modifications: car.modifications || '',
          build_status: car.build_status || 'building',
          cover_image_url: car.cover_image_url || '',
          cover_image_path: car.cover_image_path || '',
          coverImage: car.coverImage || '',
        }
      }
      this.showGarageModal = true
    },
    openJournalEditor(car, entry = null) {
      this.journalErrorMessage = ''
      this.journalStatusMessage = ''
      this.resetJournalForm()

      if (car?.id) {
        this.journalForm.garage_car_id = car.id
      }

      if (entry) {
        this.journalForm = {
          id: entry.id,
          garage_car_id: entry.garage_car_id || car?.id || '',
          title: entry.title || '',
          summary: entry.summary || '',
          entry_type: entry.entry_type || 'update',
          entry_date: entry.entry_date || new Date().toISOString().slice(0, 10),
          mileage: Number.isFinite(entry.mileage) ? entry.mileage : null,
          image_url: entry.image_url || '',
          image_path: entry.image_path || '',
          imagePreview: entry.imagePreview || '',
        }
      }

      this.showJournalModal = true
    },
    closeGarageEditor() {
      this.showGarageModal = false
      this.garageSaving = false
      this.garageErrorMessage = ''
      this.garageStatusMessage = ''
      this.resetGarageForm()
    },
    closeJournalEditor() {
      this.showJournalModal = false
      this.journalSaving = false
      this.journalErrorMessage = ''
      this.journalStatusMessage = ''
      this.resetJournalForm()
    },
    async handleGarageImageUpload(event) {
      const file = event.target.files?.[0]
      if (!file) return

      this.garageErrorMessage = ''
      this.garageStatusMessage = ''

      const { data } = await supabase.auth.getSession()
      const userId = data?.session?.user?.id

      if (!userId) {
        this.garageErrorMessage = 'Please sign in again to upload a garage photo.'
        return
      }

      let uploadFile = file
      try {
        uploadFile = await this.convertImageToWebp(file, { maxWidth: 1280, quality: 0.72, maxPixels: 1200000 })
      } catch (error) {
        this.garageErrorMessage = 'Photo processing failed. Try a different image.'
        return
      }

      const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '').replace(/\.[^.]+$/, '.webp')
      const filePath = `garage/${userId}/${Date.now()}-${safeName}`

      const { error: uploadError } = await supabase.storage
        .from('posts')
        .upload(filePath, uploadFile, { upsert: false, contentType: 'image/webp', cacheControl: '31536000' })

      if (uploadError) {
        this.garageErrorMessage = 'Upload failed. Make sure the "posts" bucket exists.'
        return
      }

      const coverImage = getPostImageUrl({ imagePath: filePath })
      this.garageForm.cover_image_path = filePath
      this.garageForm.cover_image_url = coverImage
      this.garageForm.coverImage = coverImage
      this.garageStatusMessage = 'Garage photo uploaded.'
      if (event?.target) {
        event.target.value = ''
      }
    },
    async handleJournalImageUpload(event) {
      const file = event.target.files?.[0]
      if (!file) return

      this.journalErrorMessage = ''
      this.journalStatusMessage = ''

      const { data } = await supabase.auth.getSession()
      const userId = data?.session?.user?.id

      if (!userId) {
        this.journalErrorMessage = 'Please sign in again to upload a journal photo.'
        return
      }

      let uploadFile = file
      try {
        uploadFile = await this.convertImageToWebp(file, { maxWidth: 1280, quality: 0.72, maxPixels: 1200000 })
      } catch (_error) {
        this.journalErrorMessage = 'Photo processing failed. Try a different image.'
        return
      }

      const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '').replace(/\.[^.]+$/, '.webp')
      const filePath = `build-journal/${userId}/${Date.now()}-${safeName}`

      const { error: uploadError } = await supabase.storage
        .from('posts')
        .upload(filePath, uploadFile, { upsert: false, contentType: 'image/webp', cacheControl: '31536000' })

      if (uploadError) {
        this.journalErrorMessage = 'Upload failed. Make sure the "posts" bucket exists.'
        return
      }

      const imagePreview = getPostImageUrl({ imagePath: filePath })
      this.journalForm.image_path = filePath
      this.journalForm.image_url = imagePreview
      this.journalForm.imagePreview = imagePreview
      this.journalStatusMessage = 'Journal photo uploaded.'
      if (event?.target) {
        event.target.value = ''
      }
    },
    async saveGarageCar() {
      this.garageErrorMessage = ''
      this.garageStatusMessage = ''

      const { data } = await supabase.auth.getSession()
      const userId = data?.session?.user?.id

      if (!userId) {
        this.garageErrorMessage = 'Please sign in again to save your garage.'
        return
      }

      if (!this.garageForm.make || !this.garageForm.model) {
        this.garageErrorMessage = 'Make and model are required.'
        return
      }

      this.garageSaving = true

      const payload = {
        user_id: userId,
        make: this.garageForm.make,
        model: this.garageForm.model,
        year: this.garageForm.year || null,
        trim: this.garageForm.trim,
        engine: this.garageForm.engine,
        drivetrain: this.garageForm.drivetrain,
        horsepower_stock: this.garageForm.horsepower_stock || null,
        horsepower_tuned: this.garageForm.horsepower_tuned || null,
        modifications: this.garageForm.modifications,
        build_status: this.garageForm.build_status,
        cover_image_url: this.garageForm.cover_image_url,
        cover_image_path: this.garageForm.cover_image_path,
      }

      let error = null
      if (this.garageForm.id) {
        ({ error } = await supabase.from('garage_cars').update(payload).eq('id', this.garageForm.id).eq('user_id', userId))
      } else {
        ({ error } = await supabase.from('garage_cars').insert(payload))
      }

      this.garageSaving = false

      if (error) {
        this.garageErrorMessage = error.message || 'Unable to save this car.'
        return
      }

      this.garageStatusMessage = this.garageForm.id ? 'Garage car updated.' : 'Garage car added.'
      await this.fetchGarageCars(userId)
      setTimeout(() => this.closeGarageEditor(), 500)
    },
    async saveJournalEntry() {
      this.journalErrorMessage = ''
      this.journalStatusMessage = ''

      const { data } = await supabase.auth.getSession()
      const userId = data?.session?.user?.id

      if (!userId) {
        this.journalErrorMessage = 'Please sign in again to save this entry.'
        return
      }

      if (!this.journalForm.garage_car_id) {
        this.journalErrorMessage = 'Pick a build before saving this entry.'
        return
      }

      if (!this.journalForm.title) {
        this.journalErrorMessage = 'Title is required.'
        return
      }

      if (!this.journalForm.summary) {
        this.journalErrorMessage = 'Summary is required.'
        return
      }

      this.journalSaving = true

      const payload = {
        user_id: userId,
        garage_car_id: this.journalForm.garage_car_id,
        title: this.journalForm.title.slice(0, 80),
        summary: this.journalForm.summary,
        entry_type: this.journalForm.entry_type,
        entry_date: this.journalForm.entry_date || new Date().toISOString().slice(0, 10),
        mileage: Number.isFinite(this.journalForm.mileage) ? this.journalForm.mileage : null,
        image_url: this.journalForm.image_url,
        image_path: this.journalForm.image_path,
      }

      let error = null
      if (this.journalForm.id) {
        ({ error } = await supabase.from('build_journal_entries').update(payload).eq('id', this.journalForm.id).eq('user_id', userId))
      } else {
        ({ error } = await supabase.from('build_journal_entries').insert(payload))
      }

      this.journalSaving = false

      if (error) {
        this.journalErrorMessage = error.message || 'Unable to save this journal entry.'
        return
      }

      this.journalStatusMessage = this.journalForm.id ? 'Journal entry updated.' : 'Journal entry added.'
      await this.fetchBuildJournalEntries(userId)
      setTimeout(() => this.closeJournalEditor(), 500)
    },
    confirmGarageDelete(car) {
      this.garageCarToDelete = car
      this.showDeleteGarageConfirm = true
      this.garageErrorMessage = ''
    },
    confirmJournalDelete(entry) {
      this.journalEntryToDelete = entry
      this.showDeleteJournalConfirm = true
      this.journalErrorMessage = ''
    },
    cancelGarageDelete() {
      this.showDeleteGarageConfirm = false
      this.garageDeleting = false
      this.garageCarToDelete = null
    },
    cancelJournalDelete() {
      this.showDeleteJournalConfirm = false
      this.journalDeleting = false
      this.journalEntryToDelete = null
    },
    async deleteGarageCar() {
      if (!this.garageCarToDelete?.id) {
        this.cancelGarageDelete()
        return
      }

      const { data } = await supabase.auth.getSession()
      const userId = data?.session?.user?.id
      if (!userId) {
        this.garageErrorMessage = 'Please sign in again to delete this car.'
        this.cancelGarageDelete()
        return
      }

      this.garageDeleting = true
      const { error } = await supabase
        .from('garage_cars')
        .delete()
        .eq('id', this.garageCarToDelete.id)
        .eq('user_id', userId)

      if (error) {
        this.garageDeleting = false
        this.garageErrorMessage = error.message || 'Unable to delete this car.'
        return
      }

      await this.fetchGarageCars(userId)
      await this.fetchBuildJournalEntries(userId)
      this.cancelGarageDelete()
    },
    async deleteJournalEntry() {
      if (!this.journalEntryToDelete?.id) {
        this.cancelJournalDelete()
        return
      }

      const { data } = await supabase.auth.getSession()
      const userId = data?.session?.user?.id
      if (!userId) {
        this.journalErrorMessage = 'Please sign in again to delete this entry.'
        this.cancelJournalDelete()
        return
      }

      this.journalDeleting = true
      const { error } = await supabase
        .from('build_journal_entries')
        .delete()
        .eq('id', this.journalEntryToDelete.id)
        .eq('user_id', userId)

      if (error) {
        this.journalDeleting = false
        this.journalErrorMessage = error.message || 'Unable to delete this journal entry.'
        return
      }

      await this.fetchBuildJournalEntries(userId)
      this.cancelJournalDelete()
    },
    nextMedia(post) {
      post.mediaDirection = 'next'
      post.mediaIndex = (post.mediaIndex + 1) % post.mediaUrls.length
    },
    prevMedia(post) {
      post.mediaDirection = 'prev'
      post.mediaIndex = (post.mediaIndex - 1 + post.mediaUrls.length) % post.mediaUrls.length
    },
    async saveProfile() {
      this.isSaving = true
      this.errorMessage = ''
      this.statusMessage = ''

      const { data } = await supabase.auth.getSession()
      const userId = data?.session?.user?.id

      if (!userId) {
        this.errorMessage = 'Please sign in again to update your profile.'
        this.isSaving = false
        return
      }

      const sanitizedHandle = this.sanitizeHandle(this.handle)
      const sanitizedInstagramHandle = this.sanitizeInstagramHandle(this.instagramHandle)
      const sanitizedWebsiteUrl = this.sanitizeLink(this.websiteUrl)
      const sanitizedYoutubeUrl = this.sanitizeLink(this.youtubeUrl)
      const sanitizedTiktokUrl = this.sanitizeLink(this.tiktokUrl)
      const pinnedStatus = (this.pinnedStatus || '').slice(0, 80)

      const { error } = await supabase
        .from('profiles')
        .upsert(
          {
            id: userId,
            handle: sanitizedHandle || userId.slice(0, 8),
            full_name: this.fullName,
            bio: this.bio,
            avatar_url: this.avatarUrl,
            avatar_path: this.avatarPath,
            cover_url: this.coverUrl,
            cover_path: this.coverPath,
            cover_offset_y: this.coverOffsetY,
            featured_car_id: this.featuredCarId || null,
            country: this.country,
            city: this.city,
            instagram_handle: sanitizedInstagramHandle,
            website_url: sanitizedWebsiteUrl,
            youtube_url: sanitizedYoutubeUrl,
            tiktok_url: sanitizedTiktokUrl,
            tags: this.selectedTags,
            pinned_status: pinnedStatus,
          },
          { onConflict: 'id' }
        )

      this.isSaving = false

      if (error) {
        this.errorMessage = error.message
        return
      }

      this.handle = sanitizedHandle || userId.slice(0, 8)
      this.instagramHandle = sanitizedInstagramHandle
      this.websiteUrl = sanitizedWebsiteUrl
      this.youtubeUrl = sanitizedYoutubeUrl
      this.tiktokUrl = sanitizedTiktokUrl
      this.pinnedStatus = pinnedStatus
      this.statusMessage = 'Profile updated.'
      emitAppToast({ title: 'Profile saved', message: 'Your profile updates are now live.', tone: 'success' })
    },
    async handleAvatarUpload(event) {
      const file = event.target.files?.[0]
      if (!file) return

      this.errorMessage = ''
      this.statusMessage = ''

      const { data } = await supabase.auth.getSession()
      const userId = data?.session?.user?.id

      if (!userId) {
        this.errorMessage = 'Please sign in again to upload an image.'
        return
      }

      try {
        this.revokeAvatarPreviewUrl()
        this.avatarPreviewUrl = URL.createObjectURL(file)
        const { filePath, publicUrl } = await this.uploadProfileAsset(file, { prefix: 'avatar', maxWidth: 512 })
        this.avatarPath = filePath
        this.avatarUrl = publicUrl
      } catch (error) {
        this.errorMessage = error.message || 'Photo processing failed. Try a different image.'
        return
      }

      const { error: profileError } = await this.upsertProfilePatch(userId, {
        avatar_url: this.avatarUrl,
        avatar_path: this.avatarPath,
      })

      if (profileError) {
        this.errorMessage = profileError.message || 'Unable to save profile photo.'
        return
      }

      window.dispatchEvent(
        new CustomEvent('profile:updated', {
          detail: { avatar_path: this.avatarPath, avatar_url: this.avatarUrl },
        })
      )
      this.revokeAvatarPreviewUrl()
      this.statusMessage = 'Photo updated.'
      emitAppToast({ title: 'Avatar updated', message: 'Your profile photo has been refreshed.', tone: 'success' })
    },
    async handleCoverUpload(event) {
      const file = event.target.files?.[0]
      if (!file) return

      this.errorMessage = ''
      this.statusMessage = ''

      const { data } = await supabase.auth.getSession()
      const userId = data?.session?.user?.id

      if (!userId) {
        this.errorMessage = 'Please sign in again to upload a cover.'
        return
      }

      try {
        this.revokeCoverPreviewUrl()
        this.coverPreviewUrl = URL.createObjectURL(file)
        const { filePath, publicUrl } = await this.uploadProfileAsset(file, { prefix: 'cover', maxWidth: 1600 })
        this.coverPath = filePath
        this.coverUrl = publicUrl
      } catch (error) {
        this.errorMessage = error.message || 'Cover upload failed.'
        return
      }

      const { error: profileError } = await this.upsertProfilePatch(userId, {
        cover_url: this.coverUrl,
        cover_path: this.coverPath,
        cover_offset_y: this.coverOffsetY,
      })

      if (profileError) {
        this.errorMessage = profileError.message || 'Unable to save cover photo.'
        return
      }

      this.revokeCoverPreviewUrl()
      this.statusMessage = 'Cover updated.'
      emitAppToast({ title: 'Cover updated', message: 'Your profile header now has a new cover photo.', tone: 'success' })
    },

    async deletePost(post) {
      const { error } = await supabase.from('posts').delete().eq('id', post.id)
      if (error) {
        this.errorMessage = error.message
        return
      }
      this.myPosts = this.myPosts.filter((item) => item.id !== post.id)
    },
    async confirmDeletePost() {
      if (!this.postToDelete) {
        this.showDeletePostConfirm = false
        return
      }
      await this.deletePost(this.postToDelete)
      this.postToDelete = null
      this.showDeletePostConfirm = false
    },
    async deleteProfile() {
      this.isDeleting = true
      this.errorMessage = ''
      this.statusMessage = ''

      const { data } = await supabase.auth.getSession()
      const session = data?.session
      const userId = session?.user?.id

      if (!userId) {
        this.errorMessage = 'Please sign in again to delete your profile.'
        this.isDeleting = false
        return
      }

      try {
        const cleanupResults = await Promise.all([
          supabase.from('post_likes').delete().eq('user_id', userId),
          supabase.from('post_reactions').delete().eq('user_id', userId),
          supabase.from('post_media').delete().eq('user_id', userId),
          supabase.from('garage_cars').delete().eq('user_id', userId),
          supabase.from('user_follows').delete().eq('follower_id', userId),
          supabase.from('user_follows').delete().eq('following_id', userId),
          supabase.from('posts').delete().eq('user_id', userId),
          supabase.from('profiles').delete().eq('id', userId),
        ])
        const failedStep = cleanupResults.find((result) => result.error)

        if (failedStep?.error) {
          throw failedStep.error
        }

        await supabase.auth.signOut()
        this.isDeleting = false
        this.showDeleteConfirm = false
        window.location.href = '/'
      } catch (error) {
        this.errorMessage = error.message || 'Unable to delete profile.'
        this.isDeleting = false
      }
    },
  },
}
</script>

<style>
.media-swipe-next-enter-active,
.media-swipe-next-leave-active,
.media-swipe-prev-enter-active,
.media-swipe-prev-leave-active {
  transition: opacity 220ms ease, transform 220ms ease;
}

.media-swipe-next-enter-from {
  opacity: 0;
  transform: translateX(10%);
}

.media-swipe-next-leave-to {
  opacity: 0;
  transform: translateX(-10%);
}

.media-swipe-prev-enter-from {
  opacity: 0;
  transform: translateX(-10%);
}

.media-swipe-prev-leave-to {
  opacity: 0;
  transform: translateX(10%);
}

.follow-modal-panel {
  max-height: min(80vh, 42rem);
}

.follow-modal-list {
  min-height: 0;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}
</style>
